<script lang="ts" setup>
import { useListingLocalizedContent } from '@/composables/useListingLocalizedContent';
import { useListingStore } from '@/store/modules/listing';
import type { HgImage } from '@/store/types';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { Address, Listing } from '@smg-real-estate/hg-rets-schema';
import {
  getMetaInfoAlternateLinkPropertiesForListing,
  getMetaInfoOpenGraphMetaProperties,
  getMetaInfoTwitterMetaProperties,
} from '@smg-real-estate/metatags-seo';
import type { SafeLink } from '@unhead/vue';
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import { useRoute, useRouter, type RouteLocation } from 'vue-router';

import {
  getCloudinaryUrlForTransformation,
  getCloudinaryUrlVersions,
  HgCloudinaryNamedTransformation,
} from '@/helpers/Cloudinary/CloudinaryHelper';
import { resolveUrl } from '@/helpers/HgLink/HgLinkHelper';
import {
  getAvailability,
  getAvailabilityPrefix,
  getListingDisplayPrice,
  getListingFormattedLivingSpace,
  getListingFormattedRooms,
  getListingImage,
  getListingPrice,
  getStreetWithAddition,
} from '@/helpers/ListingData/ListingData';
import { getEnhancedListingTitle } from '@/helpers/ListingData/ListingTitleData';
import { localizeLanguage } from '@/helpers/LocalizeLanguage/LocalizeLanguage';
import {
  getLdJsonByListing,
  isListingEligibleForEnhancedSeoTitle,
} from '@/helpers/MetaInfo/MetaInfoHelper';
import { isPlatformHg } from '@/helpers/platformDetection';
import { getPageRoute } from '@/helpers/Router/RouterHelper';
import { cleanupMarkupTags } from '@/helpers/TextFormatting/TextFormatting';
import { useI18n } from 'vue-i18n';

const listingStore = useListingStore();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const { listingTitle, listingDescription } = useListingLocalizedContent();

defineOptions({
  name: 'DetailPageMeta',
});

interface Props {
  listingIdFromRoute: string;
}
const props = defineProps<Props>();

const listingImages = computed<HgImage[] | undefined>(() => {
  return listingStore.listingImages;
});
const listing = computed<Listing>(() => {
  return listingStore.listing as Listing;
});
const address = computed<Address | undefined>(() => {
  return listing.value?.address;
});
const listingPrice = computed<string>(() => {
  return getListingPrice(listing.value, t);
});
const rooms = computed<string | undefined>(() => {
  return getListingFormattedRooms(listing.value, t);
});
const livingSpace = computed<string | undefined>(() => {
  return getListingFormattedLivingSpace(listing.value);
});

const truncate = (originalString: string | undefined, length: number) => {
  return originalString && originalString.length > length
    ? `${originalString.slice(0, length)}...`
    : (originalString ?? '');
};
const getTitle = (): string => {
  // The listing is eligible for enhanced SEO title generation on IS24 only,
  // and for apartments, houses, and parking spaces property types.
  if (isListingEligibleForEnhancedSeoTitle(listing.value)) {
    return getEnhancedListingTitle(listing.value, t, locale.value);
  }

  if (!address.value?.street || !getListingDisplayPrice(listing.value) || !rooms.value) {
    return listingTitle.value ?? '';
  }

  return t('metaInfo.pdp.titleTemplate', {
    listingTitle: truncate(listingTitle.value, 40),
    location: (address.value && getStreetWithAddition(address.value)) || '',
    price: listingPrice.value,
    rooms: rooms.value,
  });
};
const getDescription = (): string => {
  const category = t(`listingCategories.${listing.value.categories?.slice(-1)[0]}`);

  const cleanedListingDescription = cleanupMarkupTags(listingDescription.value ?? '');
  const offerTypeSpeak = t(`metaInfo.pdp.offerTypesSpeak.${listing.value.offerType}`);

  return t('metaInfo.pdp.descriptionTemplate', {
    location: address.value?.locality,
    category,
    price: listingPrice.value,
    offerTypeSpeak,
    description: truncate(cleanedListingDescription, 155),
    city: address.value?.locality,
    availability: `${getAvailabilityPrefix(listing.value, t)}${getAvailability(listing.value, t)}`,
    listingTitle: listingTitle.value,
    rooms: rooms.value,
    living_space: livingSpace.value,
  });
};
const getTransformedListingImageUrl = (): string | undefined => {
  if (!listingImages.value || listingImages.value.length === 0) {
    return undefined;
  }

  return getCloudinaryUrlForTransformation(
    listingImages.value[0].src,
    HgCloudinaryNamedTransformation.SMALL,
  );
};
const alternateRoutes = () => {
  const links = [
    HgSupportedLanguage.DE,
    HgSupportedLanguage.FR,
    HgSupportedLanguage.IT,
    HgSupportedLanguage.EN,
  ].map((language) => {
    const route: RouteLocation = router.resolve(
      getPageRoute('detailPage', language, listing.value.offerType, {
        params: { listingId: props.listingIdFromRoute },
      }),
    );

    return {
      lang: localizeLanguage(route.meta?.locale as HgSupportedLanguage),
      href: resolveUrl(route.fullPath),
    };
  });
  const xDefaultLink = {
    lang: 'x-default',
    href: links[0].href,
  };

  return [...links, xDefaultLink];
};
const getAlternateLinks = (): SafeLink[] => {
  const links = getMetaInfoAlternateLinkPropertiesForListing({
    listingId: props.listingIdFromRoute,
    offerTypeTranslated: t(`metaInfo.pdp.offerTypes.${listing.value.offerType}`) as string,
    alternateRoutes: alternateRoutes(),
  }) as any;

  // We dont have alternate-app links for other platforms
  if (!isPlatformHg()) {
    return links.filter((i: any) => i.vmid !== 'alternate-app');
  }

  return links.map((l: any) => ({ ...l, 'data-vmid': l.vmid }));
};
const getFirstImageLink = (): { rel: string; href: string }[] | undefined => {
  const imageUrl = getListingImage(listing.value, 0)?.url;
  if (!imageUrl) {
    return undefined;
  }
  const imageSrc = getCloudinaryUrlForTransformation(
    imageUrl,
    HgCloudinaryNamedTransformation.SMALL,
  );
  const imageExtension = getImageExtension(imageSrc);
  const mediaBaseUrl = import.meta.env.VUE_MEDIA_URL;
  const preConnect = { rel: 'preconnect', href: mediaBaseUrl, crossorigin: '' };
  const dnsPrefetch = { rel: 'dns-prefetch', href: mediaBaseUrl.replace('https://', '//') };
  const preloadImage = {
    rel: 'preload',
    as: 'image',
    imagesizes: '(min-width: 1280px) 800px, 100vw',
    imagesrcset: getCloudinaryUrlVersions(imageUrl),
    href: imageSrc,
    type: `${imageExtension ? 'image/' + imageExtension : ''}`,
    fetchpriority: 'high',
  };

  return [preConnect, dnsPrefetch, preloadImage];
};
const getImageExtension = (imageUrl: string): string => {
  const lastPart = imageUrl?.split('/').pop() ?? '';
  return lastPart.includes('.') ? (lastPart.split('.').pop() ?? '') : '';
};

useHead(() => {
  const pageTitle = getTitle();
  const pageDescription = getDescription();
  const pageUrl = resolveUrl(route.fullPath);
  const previewImage = getTransformedListingImageUrl();
  const listingCategory = (listing.value?.categories ?? []).slice(-1)[0];
  const listingCategoryTranslated =
    listingCategory && String(t(`listingCategories.${listingCategory}`));

  return {
    title: pageTitle,
    meta: [
      {
        'data-vmid': 'description',
        name: 'description',
        content: pageDescription,
      },
      ...(getMetaInfoOpenGraphMetaProperties({
        pageTitle,
        pageDescription,
        pageUrl,
        previewImage,
        baseDomain: import.meta.env.VUE_APP_BASE_URL,
      }) as any),
      ...(getMetaInfoTwitterMetaProperties({
        pageTitle,
        pageDescription,
        previewImage,
        locale: locale.value,
      }) as any),
    ],
    link: [
      {
        'data-vmid': 'canonical',
        rel: 'canonical',
        href: resolveUrl(route.path),
      },
      ...getAlternateLinks(),
      ...(getFirstImageLink() || []),
    ].filter(Boolean),
    script: getLdJsonByListing(listing.value, {
      listingUrl: pageUrl,
      listingCategory: listingCategoryTranslated,
      listingImage: previewImage,
      listingTitle: pageTitle,
      listingDescription: pageDescription,
    }),
  };
});
</script>
